import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Badge } from 'react-bootstrap';
import Business from '../../utils/images/business.jpg';
import SectionSeparator from "../../components/Separator/SectionSeparator";


import './Courses.css';

// ── Data ─────────────────────────────────────────────────────────────────────

const dataMining = [
    {
        id: 1,
        icon: "🎯",
        title: 'Targeted List by Job Title',
        description: 'By tailoring your B2B email list, you can make sure that the right professionals are seeing your marketing communications according to certain job titles. This increases the possibility of engagement and conversion — saving you time and money.',
    },
    {
        id: 2,
        icon: "🏭",
        title: 'Targeted List by Industry',
        description: 'By customizing your B2B email list based on targeted job industries, you can ensure that your marketing messages are reaching professionals who are most likely to have a genuine interest in your offerings.',
    },
    {
        id: 3,
        icon: "🌍",
        title: 'Targeted List by Country',
        description: 'Customizing your B2B email list based on targeted countries enables you to tailor your marketing messages to the cultural and business nuances of each region, ensuring your campaigns are more relevant and engaging.',
    },
];

const leadGen = [
    {
        id: 1,
        icon: "🌱",
        title: 'Lead Nurturing',
        description: 'We help you nurture your leads so that they are more likely to convert into customers. We can help you create lead magnets, send follow-up emails, and track your results.',
    },
    {
        id: 2,
        icon: "💰",
        title: 'Pay Per Lead (PPL) Lead Generation',
        description: 'The PPL lead generation strategy involves paying a third party only when they generate a qualified lead — a potential customer who has expressed interest in your product or service.',
    },
    {
        id: 3,
        icon: "📧",
        title: 'Email Marketing',
        description: 'We help you create and send email campaigns that are effective at generating leads. We can help you segment your audience, create compelling emails, and track your results.',
    },
];

const globalMarket = [
    {
        id: 1,
        icon: "♟️",
        title: 'Strategic Consulting Service',
        description: 'As consultants for strategic planning, we at LeadsQResearch view strategy as more than just a process map. Our staff works resolutely to reduce it to manageable tasks that yield meaningful outcomes.',
    },
    {
        id: 2,
        icon: "🔬",
        title: 'Research Service',
        description: 'Our analysts have developed the ability to look at the most detailed data at the lowest levels, separating it into even more detail-oriented fields. Granular data is successfully integrated and analyzed when combined with data from outside sources.',
    },
];

// ── Animated counter ──────────────────────────────────────────────────────────
const useCountUp = (target, isActive, duration = 1600) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!isActive) return;
        const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ''));
        const suffix = String(target).replace(/[0-9.]/g, '');
        let start = null;
        const step = (ts) => {
            if (!start) start = ts;
            const p = Math.min((ts - start) / duration, 1);
            const e = 1 - Math.pow(1 - p, 3);
            setCount(Math.floor(e * numeric) + suffix);
            if (p < 1) requestAnimationFrame(step);
            else setCount(target);
        };
        requestAnimationFrame(step);
    }, [isActive, target, duration]);
    return count;
};

// ── Service card ─────────────────────────────────────────────────────────────
const ServiceCard = ({ item, index, sectionVisible }) => {
    const [hovered, setHovered] = useState(false);

    return (
        <Col
            lg={4}
            md={6}
            className={`cp-col ${sectionVisible ? 'cp-col--visible' : ''}`}
            style={{ '--i': index }}
        >
            <div
                className={`cp-card ${hovered ? 'cp-card--hovered' : ''}`}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
            >
                {/* bg image */}
                <div className="cp-card__bg" style={{ backgroundImage: `url(${Business})` }} />
                <div className="cp-card__overlay" />
                <div className="cp-card__glow" />

                <div className="cp-card__content">
                    <div className="cp-card__icon">{item.icon}</div>
                    <h3 className="cp-card__title">{item.title}</h3>
                    <p className="cp-card__desc">{item.description}</p>
                    {/* bottom line sweep */}
                    <div className="cp-card__line" />
                </div>
            </div>
        </Col>
    );
};

// ── Section block ─────────────────────────────────────────────────────────────
const ServiceSection = ({ label, title, subtitle, items, badge }) => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const obs = new IntersectionObserver(
            ([e]) => { if (e.isIntersecting) setVisible(true); },
            { threshold: 0, rootMargin: '0px 0px -60px 0px' }
        );
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div className={`cp-section-block ${visible ? 'cp-section-block--visible' : ''}`} ref={ref}>
            <div className="cp-section-header">
                <Badge className="cp-section-badge">{badge}</Badge>
                <h2 className="cp-section-title">{title}</h2>
                <p className="cp-section-subtitle">{subtitle}</p>
            </div>
            <Row className="g-4">
                {items.map((item, i) => (
                    <ServiceCard key={item.id} item={item} index={i} sectionVisible={visible} />
                ))}
            </Row>
        </div>
    );
};

// ── Stats row ─────────────────────────────────────────────────────────────────
const stats = [
    { value: '50000+', label: 'Data Available' },
    { value: '6+', label: 'Countries Covered' },
    { value: '98%', label: 'Data Accuracy' },
    { value: '15+', label: 'Clients Served' },
];

// ── Single stat item — hook called at component level, not inside map ─────────
const StatItem = ({ value, label, index, visible }) => {
    const val = useCountUp(value, visible);
    return (
        <div className="cp-stat" style={{ '--i': index }}>
            <span className="cp-stat__value">{val}</span>
            <span className="cp-stat__label">{label}</span>
        </div>
    );
};

const StatsRow = () => {
    const [visible, setVisible] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.3 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, []);

    return (
        <div className={`cp-stats ${visible ? 'cp-stats--visible' : ''}`} ref={ref}>
            {stats.map((s, i) => (
                <StatItem key={i} value={s.value} label={s.label} index={i} visible={visible} />
            ))}
        </div>
    );
};

// ── Main page ─────────────────────────────────────────────────────────────────
function Courses() {
    const [headerVisible, setHeaderVisible] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setHeaderVisible(true), 80);
        return () => clearTimeout(t);
    }, []);

    return (
        <div className="courses-page">

            {/* ── Hero header ── */}
            <header className={`cp-header height-header ${headerVisible ? 'cp-header--visible' : ''}`}>
                <div className="cp-header__bg" />
                <div className="cp-header__grid" />
                <div className="cp-header__orb cp-header__orb--1" />
                <div className="cp-header__orb cp-header__orb--2" />

                <Container className="cp-header__content">
                    <div className="cp-header__badge" style={{ '--delay': '0s' }}>
                        <span className="cp-header__badge-dot" /> Our Services
                    </div>
                    <h1 className="cp-header__title" style={{ '--delay': '0.2s' }}>
                        B2B Solutions <span className="cp-header__accent">That Deliver</span>
                    </h1>
                    <p className="cp-header__text" style={{ '--delay': '0.35s' }}>
                        Are you looking for B2B email lists to scale your marketing strategies?
                        Our services are exactly what you need. The data sets we offer cover the most
                        prominent global industries, including large corporations, SMEs, and executive staff.
                    </p>
                    <div className="cp-header__and" style={{ '--delay': '0.48s' }}>
                        <span className="cp-header__and-line" />
                        <span className="cp-header__and-text">AND ALSO</span>
                        <span className="cp-header__and-line" />
                    </div>
                    <p className="cp-header__text cp-header__text--sm" style={{ '--delay': '0.58s' }}>
                        LeadsQResearch offers detailed global market and customer analyses, utilizing multiple
                        data sources to inform clients about current trends, future shifts, and actionable insights.
                    </p>
                </Container>

                <div className="cp-header__scroll">
                    <span>Scroll</span>
                    <div className="cp-header__scroll-line" />
                </div>
            </header>

            {/* ── Stats ── */}
            <div className="cp-stats-wrapper">
                <Container>
                    <StatsRow />
                </Container>
            </div>

            {/* ── Content sections ── */}
            <div className="cp-body">
                <Container>

                    <ServiceSection
                        badge="✦ Data Intelligence"
                        title="B2B Data Mining / Data Scraping"
                        subtitle="Customize your B2B data based on the filters that matter most to your pipeline."
                        items={dataMining}
                    />

                    <div className="cp-divider">
                        <span className="cp-divider__diamond" />
                    </div>

                    <ServiceSection
                        badge="✦ Growth Engine"
                        title="Lead Generation"
                        subtitle="LeadsQResearch helps companies generate more leads by creating high-quality content and building relationships with key decision-makers."
                        items={leadGen}
                    />

                    <div className="cp-divider">
                        <span className="cp-divider__diamond" />
                    </div>

                    <ServiceSection
                        badge="✦ Market Intelligence"
                        title="Study of Global Markets & Customers"
                        subtitle="Comprehensive research and strategic consulting to keep you ahead of the curve."
                        items={globalMarket}
                    />

                </Container>
            </div>
            {/* <SectionSeparator></SectionSeparator> */}

        </div>
    );
}

export default Courses;
